import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import "../../Styles/Modal.css";

function Modal({
  isOpen,
  onClose,
  children,
  maxWidth = "900px",
  ariaLabel = "Dialog",
  panelClassName = "",
  contentClassName = "",
}) {
  const panelRef = useRef(null);
  const previousActiveElementRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    previousActiveElementRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const handleKey = (event) => {
      if (event.key === "Escape") {
        onCloseRef.current?.();
      }
    };

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      panelRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      previousActiveElementRef.current?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className="modal-backdrop">
      <div className="modal-overlay" onClick={onClose} aria-hidden="true" />

      <div
        ref={panelRef}
        className={`modal-panel ${panelClassName}`.trim()}
        style={{
          "--modal-max-width": maxWidth,
        }}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
      >
        <div className="modal-toolbar">
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>

        <div className={`modal-content ${contentClassName}`.trim()}>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default Modal;
